import {Service} from "vue-typedi";
import { eventsApiClient } from "@/configuration/api-client";
import {AlertSettings} from "@/model/AlertSettings";
import { EventBus } from "@/helpers/EventBus";
import { refreshFirebaseToken } from '../main';

@Service()
export class AlertService {

    static ALERTS_ENDPOINT: string = '/alerts';
    private static readonly FCM_TOKEN_KEY = 'fcmToken';

    public getAlerts(storeId: string, zoneCode: string): Promise<AlertSettings>  {
        if (storeId == null || zoneCode == null) {
            return Promise.reject("storeId and zoneCode should be given to retrieve corresponding alerts");
        }

        const params = {
            storeId: storeId,
            zoneCode: zoneCode
        };

        return eventsApiClient.get(AlertService.ALERTS_ENDPOINT, { params: params })
            .then((response: any) => {
                const data:AlertSettings = response && response.data ? response.data : {};
                return Promise.resolve(data);
            }).catch(error => {
                return Promise.reject(error);
            });
    }

    public insertAlert(requestBody: AlertSettingsRequest) {
        if (requestBody.storeId == null || requestBody.zoneCode == null) {
            return Promise.reject("storeId and zoneCode should be given to insert this alert");
        }

        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const createAlert = () => {
            return eventsApiClient.post(AlertService.ALERTS_ENDPOINT, requestBody, config)
                .then((response: any) => {
                    const data: AlertSettings = response && response.data ? response.data : {};
                    return Promise.resolve(data);
                }).catch(error => {
                    return Promise.reject(error);
                });
        }

        return this.getValidIdentifier(requestBody.channel, requestBody.identifier)
                    .then((identifier) => {
                        requestBody.identifier = identifier;

                        return createAlert();
                    }).catch(error => {
                        return Promise.reject(error);
                    });
    }

    private getValidIdentifier(channel: AlertChannel, identifier: string): Promise<string> {
        if (channel === AlertChannel.NOTIFICATION_ON_DEVICE && !identifier) {
            return refreshFirebaseToken();
        }

        return Promise.resolve(identifier)
    }

    public updateAlert(requestBody: AlertSettingsUpdateRequest): Promise<AlertSettings[]> {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        return eventsApiClient.patch(AlertService.ALERTS_ENDPOINT, requestBody, config)
            .then((response: any) => {
                const data: AlertSettings[] = response && response.data ? response.data : {};
                return Promise.resolve(data);
            }).catch(error => {
                return Promise.reject(error);
            });
    }

    public deleteAlert(requestBody: AlertSettingsRequest): Promise<AlertSettings> {
        if (requestBody.storeId == null || requestBody.zoneCode == null) {
            return Promise.reject("storeId and zoneCode should be given to delete this alert");
        }

        const config = {
            headers: {
                'Content-Type': 'application/json'
            },
            data: requestBody
        };

        return eventsApiClient.delete(AlertService.ALERTS_ENDPOINT, config)
            .then((response: any) => {
                const data: AlertSettings = response && response.data ? response.data : {};
                return Promise.resolve(data);
            }).catch(error => {
                return Promise.reject(error);
            });
    }

    public getDeviceRegistrationId(): string {
        return localStorage.getItem(AlertService.FCM_TOKEN_KEY);
    }

    public saveDeviceRegistrationId(token: string, storeId: string) {
        const oldToken = this.getDeviceRegistrationId();
        localStorage.setItem(AlertService.FCM_TOKEN_KEY, token);
        
        this.replaceDeviceRegistrationId(oldToken, token, storeId);
    }

    private replaceDeviceRegistrationId(oldToken: string, token: string, storeId: string) {
        if (oldToken == null || oldToken === token) {
            return;
        }

        const request = {
            oldIdentifier: oldToken,
            newIdentifier: token,
            channel: AlertChannel.NOTIFICATION_ON_DEVICE,
            storeId: storeId
        } as AlertSettingsUpdateRequest;

        this.updateAlert(request).then(alertSettingsList => {
            alertSettingsList.forEach(alertSettings => {
                EventBus.$emit(EventBus.REFRESH_ALERT_SETTINGS, alertSettings);
            });
        });
    }
}

export class AlertSettingsRequest {
    identifier: string;
    channel: AlertChannel;
    storeId: string;
    zoneCode: string;
}

export class AlertSettingsUpdateRequest {
    oldIdentifier: string;
    newIdentifier: string;
    channel: AlertChannel;
    storeId: string;
    zoneCode: string;
}

export enum AlertChannel {
    EMAIL = "EMAIL",
    NOTIFICATION_ON_DEVICE = "NOTIFICATION_ON_DEVICE"
}